import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import SchoolIcon from "@mui/icons-material/School";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import { Link as RouterLink } from "react-router-dom";

import stats from "../../content/impact.json";

const pillars = [
  { icon: <MedicalServicesIcon />, label: "Reconstructive surgery" },
  { icon: <SchoolIcon />, label: "Clinical training" },
  { icon: <VolunteerActivismIcon />, label: "Long-term collaboration" },
];

const Welcome = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: { xs: "auto", md: "calc(100vh - 64px)" },
        bgcolor: theme.palette.primary.main,
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 52%, #0f5598 100%)`,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
        position: "relative",
        "&:before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 22% 12%, rgba(255,255,255,0.16), transparent 28%), linear-gradient(120deg, rgba(255,255,255,0.08), transparent 48%)",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          maxWidth: 1180,
          px: { xs: 2.5, sm: 4 },
          py: { xs: 6, md: 9 },
          gap: { xs: 4, md: 7 },
        }}
      >
        <Box sx={{ flex: 1.05, order: { xs: 2, md: 1 } }}>
          <Typography
            variant="overline"
            sx={{
              color: theme.palette.common.white,
              letterSpacing: 2,
              fontWeight: 800,
              opacity: 0.86,
            }}
          >
            Cyrenaeus
          </Typography>
          <Typography
            variant="h2"
          component="h1"
            color={theme.palette.common.white}
            sx={{
              mt: 1,
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontStyle: "italic",
              fontWeight: 500,
              maxWidth: 680,
              fontSize: { xs: "2.5rem", sm: "3.6rem", md: "4.6rem" },
              lineHeight: 1.03,
            }}
          >
            Reconstructive surgery, teaching and care.
          </Typography>
          <Typography
            color="rgba(255,255,255,0.86)"
            sx={{
              mt: 2.5,
              maxWidth: 650,
              fontSize: { xs: "1rem", md: "1.12rem" },
              lineHeight: 1.75,
            }}
          >
            Cyrenaeus brings specialist clinicians to partner hospitals in Africa
            where plastic reconstructive surgery is limited or unavailable. We
            provide direct treatment while building long-term training
            relationships with local doctors and nurses.
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{
              mt: 3,
              "& .MuiButton-root": {
                minHeight: 48,
                px: 3,
              },
            }}
          >
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              component={RouterLink}
              to="/about"
              sx={{
                bgcolor: theme.palette.common.white,
                color: theme.palette.primary.main,
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.9)",
                  transform: "translateY(-2px)",
                },
                transition: "all 180ms ease",
              }}
            >
              About the charity
            </Button>
            <Button
              variant="contained"
              component={RouterLink}
              to="/donate"
              sx={{
                bgcolor: theme.palette.donate.primary,
                "&:hover": {
                  backgroundColor: theme.palette.donate.secondary,
                  transform: "translateY(-2px)",
                },
                color: theme.palette.common.black,
                transition: "all 180ms ease",
              }}
            >
              Donate
            </Button>
          </Stack>
          <Chip
            label="UK registered charity · 1202741"
            sx={{
              mt: 3,
              bgcolor: "rgba(255,255,255,0.08)",
              color: "rgba(255,255,255,0.86)",
              border: "1px solid rgba(255,255,255,0.16)",
            }}
          />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ mt: 4 }}
          >
            {pillars.map((pillar) => (
              <Box
                key={pillar.label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: theme.palette.common.white,
                  bgcolor: "rgba(255,255,255,0.1)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                  backdropFilter: "blur(10px)",
                  "& svg": {
                    color: theme.palette.donate.primary,
                    fontSize: 20,
                  },
                }}
              >
                {pillar.icon}
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {pillar.label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
        <Box
          sx={{
            flex: 0.95,
            order: { xs: 1, md: 2 },
            width: "100%",
            position: "relative",
          }}
        >
          <Box
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 30px 70px rgba(0,0,0,0.34)",
              transform: { md: "rotate(1.2deg)" },
              transition: "transform 260ms ease",
              "&:hover": {
                transform: { md: "rotate(0deg) scale(1.015)" },
              },
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/images/cyrenaeus.jpg`}
              alt="Cyrenaeus clinicians working with hospital partners"
              loading="eager"
              decoding="async"
              fetchPriority="high"
              sx={{
                width: "100%",
                height: { xs: 300, sm: 420, md: 560 },
                objectFit: "cover",
              }}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1,
              mt: { xs: 1.5, md: -5 },
              mx: { xs: 0, md: 3 },
              position: "relative",
            }}
          >
            {stats.map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  bgcolor: theme.palette.common.white,
                  color: theme.palette.primary.main,
                  p: { xs: 1.25, sm: 1.75 },
                  borderRadius: 2,
                  boxShadow: "0 16px 40px rgba(0,0,0,0.2)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h5"
                  component="p"
                  sx={{ fontWeight: 900, lineHeight: 1 }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontWeight: 700 }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Welcome;
